import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle
} from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { baseUrl } from "../Univ.js";
import "leaflet/dist/leaflet.css";

// 🔥 Icons
const greenIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
  iconSize: [40, 40],
});

const redIcon = new L.Icon({
  iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  iconSize: [40, 40],
});

const OVERHEAT_TEMP = 50;

// 🏭 Geofence
const GEOFENCE_CENTER = [17.385, 78.4867];
const GEOFENCE_RADIUS = 5000;

function IOTMapPage() {

  const [devices, setDevices] = useState([]);
  const [routes, setRoutes] = useState({});

  // -----------------------------
  // LOAD INITIAL DATA
  // -----------------------------
  useEffect(() => {
    fetchInitialData();

    const socket = new WebSocket("ws://localhost:8000/ws/iot/");

    socket.onmessage = (event) => {
      const liveData = JSON.parse(event.data);

      if (liveData.latitude && liveData.longitude) {

        // UPDATE DEVICE LIST
        setDevices(prev => {
          const others = prev.filter(
            d => d.device_id !== liveData.device
          );

          return [
            ...others,
            {
              device_id: liveData.device,
              latitude: liveData.latitude,
              longitude: liveData.longitude,
              temperature: liveData.temperature,
              humidity: liveData.humidity
            }
          ];
        });

        // UPDATE ROUTES
        setRoutes(prev => ({
          ...prev,
          [liveData.device]: [
            ...(prev[liveData.device] || []),
            [liveData.latitude, liveData.longitude]
          ]
        }));

        // GEOFENCE CHECK
        const distance = getDistanceFromLatLonInKm(
          liveData.latitude,
          liveData.longitude,
          GEOFENCE_CENTER[0],
          GEOFENCE_CENTER[1]
        );

        if (distance * 1000 > GEOFENCE_RADIUS) {
          alert(`🚨 ${liveData.device} exited geofence!`);
        }
      }
    };

    return () => socket.close();
  }, []);

  // -----------------------------
  // FETCH HISTORY
  // -----------------------------
  const fetchInitialData = () => {
    axios.get(baseUrl + "iot-data/")
      .then(res => {

        const latest = {};
        const routeData = {};

        res.data.forEach(item => {

          if (item.latitude && item.longitude) {

            const devId = item.device.device_id;

            latest[devId] = {
              device_id: devId,
              latitude: item.latitude,
              longitude: item.longitude,
              temperature: item.temperature,
              humidity: item.humidity
            };

            if (!routeData[devId]) {
              routeData[devId] = [];
            }

            routeData[devId].push([
              item.latitude,
              item.longitude
            ]);
          }
        });

        setDevices(Object.values(latest));
        setRoutes(routeData);
      });
  };

  // -----------------------------
  // DISTANCE UTILS
  // -----------------------------
  function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className="container-fluid mt-3">

      <h4>🚚 Industrial GPS Tracking Dashboard</h4>

      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        style={{ height: "85vh", width: "100%" }}
      >

        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <Circle
          center={GEOFENCE_CENTER}
          radius={GEOFENCE_RADIUS}
        />

        {devices.map(device => {

          const selectedIcon =
            device.temperature > OVERHEAT_TEMP
              ? redIcon
              : greenIcon;

          return (
            <React.Fragment key={device.device_id}>

              {routes[device.device_id] && (
                <Polyline
                  positions={routes[device.device_id]}
                />
              )}

              <Marker
                position={[
                  device.latitude,
                  device.longitude
                ]}
                icon={selectedIcon}
              >
                <Popup>
                  <strong>{device.device_id}</strong><br />
                  🌡 Temp: {device.temperature} °C<br />
                  💧 Humidity: {device.humidity} %

                  {device.temperature > OVERHEAT_TEMP && (
                    <div style={{ color: "red" }}>
                      🔥 OVERHEAT ALERT
                    </div>
                  )}
                </Popup>
              </Marker>

            </React.Fragment>
          );
        })}

      </MapContainer>

    </div>
  );
}

export default IOTMapPage;
